/**
 * API Configuration
 * Update this with your PHP backend URL
 */

// API Base URL - defaults to production, falls back to localhost for development
const getApiBaseUrl = () => {
  // Use environment variable if set
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL.replace(/\/+$/, ''); // Remove trailing slashes
  }
  
  // Development - Dental Hub uses port 8001 to avoid conflicts
  // When using PHP built-in server with -t backend, the path is /api/ not /backend/api/
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    // Try /api/ first (for PHP built-in server with -t backend), fallback to /backend/api/ (for full server setup)
    return 'http://localhost:8001/api';
  }
  
  // Production: Use /api/api/ path since files are in api/api/ folder
  // Structure on server: api/api/ (PHP files), api/config/ (config files)
  return `${window.location.protocol}//${window.location.hostname}/api/api`;
};

export const API_BASE_URL = getApiBaseUrl();

// Always log API URL for debugging (even in production)
console.log('API Base URL:', API_BASE_URL);

export const apiRequest = async (endpoint, options = {}) => {
  // Check if this is a login/registration endpoint (these don't require auth)
  const isLoginEndpoint = endpoint.includes('_login') || 
                          endpoint.includes('login.php') || 
                          endpoint.includes('register') ||
                          endpoint === 'login.php' ||
                          endpoint === 'register.php';
  
  // Determine which token to use based on endpoint type
  const isAdminEndpoint = endpoint.includes('admin_') && !isLoginEndpoint;
  const isHospitalEndpoint = endpoint.includes('hospital_') && !isAdminEndpoint && !isLoginEndpoint;
  
  // Check if this is a patient-specific endpoint
  // Note: submit_patient_record.php and get_patient_record_submissions.php are used by doctors too, so exclude them from patient-only endpoints
  // Also exclude get_next_patient_id.php and check_patient_id.php as they are used by doctors/admins
  // Exclude endpoints that doctors/admins use to manage patients
  const excludedFromPatientEndpoints = [
    'submit_patient_record',
    'get_patient_record_submissions',
    'get_next_patient_id',
    'check_patient_id',
    'get_patient_by_phone',
    'add_patient',
    'update_patient',
    'delete_patient'
  ];
  
  const isExcluded = excludedFromPatientEndpoints.some(excluded => endpoint.includes(excluded));
  
  const isPatientEndpoint = (endpoint.includes('patient_') && !isExcluded) || 
                            endpoint.includes('get_patient_own_records') ||
                            endpoint === 'get_patient_own_records.php';
  
  // Debug log for endpoint classification
  if (endpoint.includes('patient_') || endpoint.includes('get_next_patient_id') || endpoint.includes('check_patient_id')) {
    console.log(`[API] Endpoint classification for "${endpoint}":`, {
      includesPatient: endpoint.includes('patient_'),
      isExcluded,
      isPatientEndpoint,
      excludedMatches: excludedFromPatientEndpoints.filter(excluded => endpoint.includes(excluded)),
      willUsePatientToken: isPatientEndpoint
    });
  }
  
  let token = null;
  
  if (isLoginEndpoint) {
    // Login/registration endpoints don't require authentication
    token = null;
  } else if (isAdminEndpoint) {
    // For admin endpoints, prioritize admin session token
    token = localStorage.getItem('admin_session_token');
    if (!token) {
      console.warn(`Admin endpoint ${endpoint} called without admin_session_token`);
    }
  } else if (isHospitalEndpoint) {
    // For hospital endpoints, use hospital session token
    token = localStorage.getItem('hospital_session_token');
  } else if (isPatientEndpoint) {
    // For patient endpoints, ONLY use session_token (patient/user token)
    // Do NOT use hospital_session_token or admin_session_token
    // Explicitly check all tokens to ensure we're using the right one
    const sessionToken = localStorage.getItem('session_token');
    const hospitalToken = localStorage.getItem('hospital_session_token');
    const adminToken = localStorage.getItem('admin_session_token');
    
    console.log(`Patient endpoint ${endpoint} - Token check:`, {
      has_session_token: !!sessionToken,
      has_hospital_token: !!hospitalToken,
      has_admin_token: !!adminToken,
      session_token_length: sessionToken?.length || 0,
      hospital_token_length: hospitalToken?.length || 0
    });
    
    token = sessionToken;
    if (!token) {
      console.warn(`Patient endpoint ${endpoint} called without session_token`);
    } else {
      console.log(`Patient endpoint ${endpoint}: Using session_token (patient token), first 20 chars: ${token.substring(0, 20)}...`);
    }
  } else {
    // For other endpoints (including doctor/admin patient management endpoints like get_next_patient_id.php),
    // prioritize hospital_session_token for doctors/admins, then admin token, then patient token
    // This ensures doctors can use get_next_patient_id.php, check_patient_id.php, etc. with their hospital token
    token = localStorage.getItem('hospital_session_token') || 
            localStorage.getItem('admin_session_token') ||
            localStorage.getItem('session_token');
    
    // Debug log for non-patient endpoints that deal with patients
    if (endpoint.includes('patient_') || endpoint.includes('get_next_patient_id') || endpoint.includes('check_patient_id')) {
      console.log(`[API] Non-patient endpoint "${endpoint}" - Token selection:`, {
        hasHospitalToken: !!localStorage.getItem('hospital_session_token'),
        hasAdminToken: !!localStorage.getItem('admin_session_token'),
        hasSessionToken: !!localStorage.getItem('session_token'),
        selectedToken: token ? (token === localStorage.getItem('hospital_session_token') ? 'hospital_token' : 
                               token === localStorage.getItem('admin_session_token') ? 'admin_token' : 
                               'session_token') : 'none'
      });
    }
  }
  
  const defaultHeaders = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
    // Debug: Log which token is being sent for patient endpoints
    if (isPatientEndpoint) {
      console.log(`Patient endpoint ${endpoint}: Sending Authorization header with token (first 20 chars): ${token.substring(0, 20)}...`);
      // Verify we're NOT accidentally using hospital token
      const hospitalToken = localStorage.getItem('hospital_session_token');
      if (hospitalToken && token === hospitalToken) {
        console.error('ERROR: Patient endpoint is using hospital_session_token instead of session_token!');
        console.error('session_token:', localStorage.getItem('session_token'));
        console.error('hospital_session_token:', hospitalToken);
      }
    }
  } else if (isPatientEndpoint) {
    console.error(`ERROR: Patient endpoint ${endpoint} called without any token!`);
  }
  
  // Add hospital_id to request if available (for tenant-specific endpoints)
  // But NOT for admin endpoints, login endpoints, or patient endpoints
  // Patient endpoints get hospital_id from the patient's session, not from hospital_data
  const hospitalToken = localStorage.getItem('hospital_session_token');
  if (hospitalToken && !isAdminEndpoint && !isLoginEndpoint && !isPatientEndpoint) {
    const hospitalData = localStorage.getItem('hospital_data');
    if (hospitalData) {
      try {
        const hospital = JSON.parse(hospitalData);
        // Add hospital_id to query params for GET requests
        // But only if it's not already in the URL
        if (options.method === 'GET' || !options.method) {
          // Check if hospital_id is already in the endpoint URL
          const hasHospitalId = endpoint.includes('hospital_id=');
          if (!hasHospitalId) {
            const separator = endpoint.includes('?') ? '&' : '?';
            endpoint = `${endpoint}${separator}hospital_id=${hospital.id}`;
          }
        } else if (options.body) {
          // For POST/PUT requests, add to body
          try {
            const bodyData = JSON.parse(options.body);
            bodyData.hospital_id = hospital.id;
            options.body = JSON.stringify(bodyData);
          } catch (e) {
            // If body is not JSON, skip
          }
        }
      } catch (e) {
        console.warn('Failed to parse hospital data:', e);
      }
    }
  }
  
  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };
  
  // Clean endpoint - remove leading slashes and 'api/' prefix if present
  const cleanEndpoint = endpoint.replace(/^\/+/, '').replace(/^api\//, '');
  const url = `${API_BASE_URL}/${cleanEndpoint}`;
  
  // Log the actual URL being called for debugging
  console.log('API Request URL:', url);
  
  try {
    const response = await fetch(url, config);
    
    // Get response text first to check if it's JSON
    const text = await response.text();
    
    // Try to parse as JSON
    let data;
    try {
      data = JSON.parse(text);
    } catch (parseError) {
      // If it's not JSON, it's likely an HTML error page
      console.error('API returned non-JSON response. URL:', url);
      console.error('Response status:', response.status);
      console.error('Response headers:', Object.fromEntries(response.headers.entries()));
      console.error('Response text (first 1000 chars):', text.substring(0, 1000));
      
      // Try alternative paths if main path fails
      if (!url.includes('/api/api/')) {
        console.warn('Trying alternative path with /api/api/');
        const altUrl = url.replace('/api/', '/api/api/');
        console.log('Trying alternative URL:', altUrl);
        // Don't auto-retry, just log for now
      }
      
      throw new Error(`Server error: Received HTML instead of JSON. Check if API endpoint exists at ${url}. Response status: ${response.status}`);
    }
    
    if (!response.ok) {
      // Use message if available, otherwise error, otherwise generic message
      const errorMessage = data.message || data.error || `Request failed (${response.status})`;
      const error = new Error(errorMessage);
      // Attach response data for better error handling
      error.response = data;
      error.status = response.status;
      // Log debug info if available
      if (data.debug) {
        console.error('API Error Debug Info:', data.debug);
      }
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('API Request Error:', error);
    throw error;
  }
};

